import { z } from "zod";
import { strictObject } from "./requests.js";
import { INT4_MAX } from "./field-shapes.js";
import { userRoles, type UserRole } from "./vocabulary.js";
import type { SessionUser } from "./wire.js";

/** Canonical sign-in identifier. Display casing is never an identity boundary. */
export const normalizedEmail = z
  .string()
  .trim()
  .email()
  .max(254)
  .transform((value) => value.toLowerCase());

/**
 * The shortest password TruckBuddy accepts. One definition, because the sign-in
 * form, the set-password page and the API each used to spell 15 on their own.
 */
export const MIN_PASSWORD_LENGTH = 15;

/** Passwords are not trimmed: spaces may be intentional password characters. */
export const password = z.string().min(MIN_PASSWORD_LENGTH).max(128);

export const loginBody = strictObject({ email: normalizedEmail, password });
export type LoginBody = z.output<typeof loginBody>;

export const changePasswordBody = strictObject({
  currentPassword: z.string().min(1).max(128),
  newPassword: password,
});
export type ChangePasswordBody = z.output<typeof changePasswordBody>;

/**
 * The emailed token, as it arrives in a link and in a body. Bounded on both ends:
 * `issueToken()` makes 43 characters of base64url, so the floor refuses anything
 * that cannot be one without trusting the hash lookup to do it.
 */
export const emailedToken = z.string().min(32).max(256);

export const requestPasswordResetBody = strictObject({ email: normalizedEmail });
export const confirmPasswordResetBody = strictObject({
  token: emailedToken,
  newPassword: password,
});

/**
 * Accept an emailed invitation: choose a password and, optionally, correct the
 * name the administrator typed. The email is NOT editable here, because it is the
 * sign-in identity and the administrator, not the invitee, decides who it is.
 */
export const acceptInviteBody = strictObject({
  token: emailedToken,
  newPassword: password,
  displayName: z.string().trim().max(200).optional(),
});
export type AcceptInviteBody = z.output<typeof acceptInviteBody>;

/**
 * Asking what a link says is a POST with the token in the BODY, not a GET with it
 * in the query string. The API logs every request's URL, and a token in a URL is a
 * live credential sitting in the container logs for as long as they are kept. The
 * emailed link itself carries the token in the URL FRAGMENT (`/set-password#token=`),
 * which a browser never sends to any server, so it reaches no log, no proxy and no
 * Referer header, and the page reads it and posts it here.
 *
 * Still read-only despite the verb: it never spends the link.
 */
export const emailedTokenBody = strictObject({ token: emailedToken });

/**
 * How long a "forgot password" link works, in minutes. The database sets it
 * (`tb_auth_create_reset`, migration 0072); this copy exists so the email and the
 * page can say it, and `password-reset.dbtest` fails if the two ever differ.
 */
export const PASSWORD_RESET_VALID_MINUTES = 30;

/**
 * What a link says, without spending it. `used` and `expired` are separate states
 * because they need different next steps (sign in, versus ask for a new link), and
 * `email` / `displayName` come back only for `valid` so a dead link tells a
 * stranger nothing.
 */
export type InviteLinkState = "valid" | "used" | "expired" | "invalid";
export interface InviteStateResponse {
  state: InviteLinkState;
  email: string | null;
  displayName: string | null;
}

/** `valid` is all a reset link may say: there is no account to describe. */
export interface PasswordResetStateResponse {
  valid: boolean;
}

/** The one answer to "forgot password", whether or not the address has an account. */
export interface PasswordResetRequestResponse {
  ok: true;
  message: string;
}

export const adminSetUserAccessBody = strictObject({
  status: z.enum(["active", "inactive"]),
  roles: z.array(z.enum(userRoles)).max(userRoles.length),
});
export type AdminSetUserAccessBody = z.output<typeof adminSetUserAccessBody>;

/**
 * Create an account and issue its first password.
 *
 * `roles` is `.min(1)` where `adminSetUserAccessBody` is not, and the asymmetry
 * is deliberate rather than an oversight: a new account is always created
 * ACTIVE, and `tb_auth_admin_create_user` refuses an active user with no roles.
 * Set-access has to allow an empty set because deactivating someone strips their
 * roles legitimately.
 *
 * `displayName` is optional — the routine falls back to the email local part
 * rather than refusing the whole request over a cosmetic field.
 */
export const adminCreateUserBody = strictObject({
  email: normalizedEmail,
  displayName: z.string().trim().max(200).optional(),
  temporaryPassword: password,
  roles: z.array(z.enum(userRoles)).min(1).max(userRoles.length),
});
export type AdminCreateUserBody = z.output<typeof adminCreateUserBody>;

/**
 * Invite someone by email: no password is typed, because none is ever shown.
 * The account is created with a placeholder nobody knows and the person chooses
 * their own through the emailed link. Same fields as `adminCreateUserBody`
 * minus the password.
 */
export const adminInviteUserBody = strictObject({
  email: normalizedEmail,
  displayName: z.string().trim().max(200).optional(),
  roles: z.array(z.enum(userRoles)).min(1).max(userRoles.length),
});
export type AdminInviteUserBody = z.output<typeof adminInviteUserBody>;

export const adminResetPasswordBody = strictObject({ temporaryPassword: password });

/**
 * How an invitation email went. `sent` means the relay accepted it; `skipped`
 * means email is not configured here (or the person has no usable address);
 * `failed` means it was configured and the relay refused. In the last two the
 * administrator still gets the link, so email is never the only way in.
 */
export type InviteDeliveryStatus = "sent" | "skipped" | "failed";
export interface InviteDelivery {
  status: InviteDeliveryStatus;
  /** A sentence for the administrator, already worded. */
  message: string;
}

/**
 * The result of inviting or re-inviting someone.
 *
 * `inviteUrl` is the ONE place the raw token leaves the server other than the
 * email itself: it is returned to the administrator who just created it so they
 * can copy it when the email could not be sent. It is never stored or logged.
 * Null when no public base URL is configured, in which case there is no link to
 * build.
 */
export interface AdminInviteResult {
  userId: string;
  delivery: InviteDelivery;
  inviteUrl: string | null;
  expiresAt: string;
}

/** An invitation still waiting to be accepted, as the Users screen shows it. */
export interface AdminInviteState {
  status: "pending" | "expired";
  expiresAt: string;
}

export interface LoginResponse {
  user: SessionUser;
  mustChangePassword: boolean;
}

export interface AdminUserRow {
  userId: string;
  email: string;
  displayName: string;
  status: "active" | "inactive";
  loginEnabled: boolean;
  mustChangePassword: boolean;
  roles: UserRole[];
  /**
   * The MaintainX history import created this person (migration 0268): they are
   * history, can never sign in, and the API refuses to give them access or a
   * password. Settings → Users lists them in a group of their own.
   */
  importedFromMainx: boolean;
  /**
   * Set while this person has been invited and has not yet chosen a password,
   * null once they have (or if they were never invited). Decided by the database
   * from the account, not from token rows alone; see `tb_invite_outstanding`.
   */
  invite: AdminInviteState | null;
}

/* ── The collector switchboard ────────────────────────────────────────
 *
 * Which vendor API pulls are allowed to run. Two states per switch: `true` on,
 * `false` off.
 *
 * ── The third state was removed on 3 September 2026 ─────────────────
 * This read *"Three-state on every switch: `true` on, `false` off, `null`
 * **inherit the deployed configuration**"*, and `collectorSwitchValue` was
 * `z.boolean().nullable()`. Eden: *"there shouldnt be NULLs in a boolean
 * switch"* — and the inherit state turned out to point at nothing stored. What
 * it inherited was the Container App Job's own trigger, which lives in Bicep;
 * `collectorDecision` resolved it to ALLOWED, identically to `true`. See
 * `collector_settings` in `packages/db/src/schema.ts`.
 *
 * Still NEITHER `.nullable()` NOR `.optional()`, and that part of the original
 * design survives intact: an omitted field would mean "leave it alone", and a
 * routine that patched one switch at a time could not carry a whole-board audit
 * delta or a single `expectedVersion`. The screen sends the COMPLETE board every
 * time, the same way the permission screen sends a whole roles array, and
 * `set_collector_settings` replaces it wholesale — which is now enforced by the
 * routine, which refuses a NULL switch rather than guessing.
 */
export const collectorSwitchValue = z.boolean();

export const setCollectorSettingsBody = strictObject({
  /**
   * The version the screen was showing when the operator started.
   *
   * Refused if it has moved. Two people on this screen during the same incident
   * is the LIKELY case rather than an exotic one, and last-write-wins would
   * silently re-enable a collector somebody had just turned off.
   */
  // `set_collector_settings` takes this as an `integer`, so its type bounds it.
  expectedVersion: z.number().int().nonnegative().max(INT4_MAX),
  masterEnabled: collectorSwitchValue,
  collectSamsara: collectorSwitchValue,
  collectMainxList: collectorSwitchValue,
  collectMainxDetail: collectorSwitchValue,
  collectSylectus: collectorSwitchValue,
  /**
   * Requests per minute, per VENDOR. Null means the built-in default, NOT
   * unlimited — there is no way to express "no cap" and that is deliberate.
   *
   * Per vendor and not per switch because a vendor enforces its quota per API
   * KEY across everything you send -- the MaintainX list and detail jobs draw on
   * one allowance, so two separate caps could each be honoured while the vendor
   * still saw twice the agreed rate.
   *
   * `.min(1)`: zero would be a second, quieter way of saying "off", which the
   * switches already say plainly. The database refuses it too.
   */
  samsaraMaxPerMinute: z.number().int().min(1).max(10_000).nullable(),
  mainxMaxPerMinute: z.number().int().min(1).max(10_000).nullable(),
  sylectusMaxPerMinute: z.number().int().min(1).max(10_000).nullable(),
  /**
   * Per-target overrides, keyed by target name -- and ONLY the targets that are
   * overridden. A target absent from this map follows its group, so omitting a key
   * is how a target is returned to inheriting, and the routine deletes its row.
   *
   * The KEYS are not validated here. They cannot be: the authoritative list lives
   * in `packages/db`, which the browser must never import, and a copy in this file
   * would be a third one to drift. The route checks them against
   * `governedTargets()` and answers with a sentence naming the bad name; the CHECK
   * constraint on the table is the backstop behind that. What zod does here is
   * bound the SHAPE and the size, so a hostile payload cannot arrive as a
   * thousand-key object.
   */
  targetOverrides: z.record(z.string().min(1).max(120), z.boolean()).refine(
    (value) => Object.keys(value).length <= 64,
    { message: "too many target overrides" },
  ),
});
export type SetCollectorSettingsBody = z.output<typeof setCollectorSettingsBody>;

export interface CollectorTargetState {
  /** `integration.resource`, as `scripts/collect.mjs` names it. */
  target: string;
  /** The group switch that governs it when no override exists. */
  switchKey: string;
  /** The stored override, or null when the target follows its group. */
  override: boolean | null;
  /** Whether it would actually be allowed to run right now. */
  effective: boolean;
  /**
   * Why, in the resolver's own words -- "switched off", "switched on",
   * "master switch is off", "inherits the deployment". The master case is the one
   * the screen must surface: a target can read ON and still be stopped.
   */
  reason: string;
}

export interface CollectorSettingsResponse {
  /**
   * On or off, never null. An absent board is reported as every switch `true` —
   * the permissive state, matching `collectorDecision`'s fail-open — rather than
   * as a null the screen would have to render as a third position.
   */
  masterEnabled: boolean;
  collectSamsara: boolean;
  collectMainxList: boolean;
  collectMainxDetail: boolean;
  collectSylectus: boolean;
  /** Requests per minute, per vendor. Null means the built-in default, never unlimited. */
  samsaraMaxPerMinute: number | null;
  mainxMaxPerMinute: number | null;
  sylectusMaxPerMinute: number | null;
  /**
   * What each null above resolves to, so the screen can say which number it is
   * inheriting without knowing the number itself.
   *
   * Served rather than imported. `DEFAULT_RATE_PER_MINUTE` lives in
   * `packages/db`, which the front end must never depend on — that package holds
   * the Postgres URL, and reaching for it would pull `drizzle-orm` and
   * `postgres` into a browser bundle. Copying the numbers into the screen
   * instead is what this replaces: they were duplicated as 60/60/20 and would
   * have drifted the first time a default changed.
   */
  defaults: { samsara: number; mainx: number; sylectus: number };
  /**
   * Every governed target with its override, its RESOLVED state, and why.
   *
   * `effective` and `reason` are computed on the server by the same
   * `collectorDecision` the collector itself calls, rather than re-derived in the
   * browser. One resolver, so the screen cannot disagree with the thing that
   * actually decides -- which is the failure this whole panel exists to avoid: a
   * toggle that reads ON for a collector that is not running.
   */
  targets: CollectorTargetState[];
  version: number;
  updatedAt: string | null;
  updatedBy: string | null;
  /** False when the caller may look but not flip. The route refuses either way. */
  canEdit: boolean;
}

/* ── Whether the collectors are actually working ──────────────────────
 *
 * A SEPARATE call from the switchboard, deliberately, and SigmaBuddy's own
 * switchboard gives the reason: health is read-only and independent of the
 * switches, so a failing health query must not blank the controls somebody opened
 * the page to use.
 *
 * ── What is here is bounded by what the database actually records ────
 *
 * `ingest_run` is a SUCCESS log, and that is structural rather than incidental:
 * the "started" row and the update to "ok" happen inside one transaction, so a
 * collector that throws rolls its own row away. Measured on the developer
 * database: 20 rows, all `ok`, none carrying an error. So this type deliberately
 * cannot express a success rate or a failure history — both would need a ledger
 * that survives the failing transaction, which does not exist yet.
 *
 * What it can say honestly is when each feed last succeeded, how much it brought
 * back, how long it took, and — from `sync_state`, which IS written outside the
 * transaction — the most recent failure, latest-only.
 */
export interface CollectorHealthRow {
  /** `integration.resource`, matching a collect target where one exists. */
  target: string;
  integration: string;
  resource: string;
  /**
   * Which switch governs this feed, or null when nothing does.
   *
   * Resolved on the server because the mapping lives in `packages/db`, which the
   * browser must never import. Null is a real answer, not a gap: `assets_export`
   * and `g060` are landed by hand from a file and no switch governs them.
   */
  switchKey: string | null;
  /** Last run that committed. Null means this feed has never run here. */
  lastOkAt: string | null;
  lastRecordCount: number | null;
  /** Wall clock of the last run, when it recorded a finish. */
  lastDurationMs: number | null;
  okRuns: number;
  /**
   * The most recent failure, from `sync_state` rather than `ingest_run`.
   * Latest-only by construction — that table holds one row per feed — so there
   * is no history behind this and the screen must not imply otherwise.
   */
  lastFailureAt: string | null;
  lastFailureError: string | null;
  /** Windows explicitly recorded as missed and not yet resolved. */
  openGaps: number;
  /**
   * When the currently-open outage began — the last point this feed provably had
   * data. Null when nothing is open.
   *
   * This is the "since when" that `lastFailureAt` cannot answer: sync_state holds
   * one row per feed and keeps only the latest error, so a collector failing every
   * two hours for a week looks identical to one that failed five minutes ago.
   */
  openGapSince: string | null;
  /** Why the open outage is open. Null when nothing is open. */
  openGapReason: string | null;
  /**
   * Outages that ended. Each is one closed `sync_gap` row with a real span, which
   * is the failure HISTORY this response used to declare absent.
   */
  resolvedGaps: number;

  /* ── What is supposed to refresh this feed ────────────────────────────
   * Resolved on the server from `COLLECTOR_SCHEDULE`, so the screen and the API
   * agree about the cadence. The screen classifies freshness itself, from these
   * plus `serverTime`, using `classifyCollectorFreshness` — the verdict then
   * refreshes as the page re-renders rather than being frozen at fetch time.
   */
  /** The Container Apps Job that collects it, or null when no Job does. */
  jobKey: string | null;
  /** The UTC cron, or null for a Manual Job and for a feed no Job collects. */
  cron: string | null;
  /**
   * Expected refresh interval in minutes once staging is armed. NULL means nothing
   * is supposed to refresh this, and such a feed can NEVER be stale — it is not a
   * missing value to be defaulted.
   */
  expectedIntervalMinutes: number | null;
  /** Whether the Job carries a cron at all. False for Sylectus in every environment. */
  scheduled: boolean;
}

/**
 * A vendor lease held right now, from `pg_locks`.
 *
 * The HOLDER, not the age — `pg_stat_activity` masks another role's session, so
 * read as `tb_app` a `tb_sync` backend returns its pid with `state` and
 * `backend_start` NULL. Recovering them needs `pg_read_all_stats`, which would give
 * the application role the query text of every session in the database.
 *
 * So this is half a diagnosis on its own and the whole of one alongside the feed's
 * freshness: a lease held with a recent success is a run in progress; a lease held
 * while every feed for that vendor reads stale is a crashed execution still holding
 * it, and `pid` is what a DBA terminates.
 */
export interface CollectorLeaseHolderView {
  vendor: string;
  pid: number;
}

export interface CollectorHealthResponse {
  rows: CollectorHealthRow[];
  /**
   * The DATABASE's clock at the moment of the read, as the reference for every
   * freshness verdict.
   *
   * Not the browser's clock. `lastOkAt` comes from Postgres, so comparing it to a
   * viewer's `Date.now()` would fold that machine's clock error into the verdict —
   * and a laptop an hour ahead would report every feed as late.
   */
  serverTime: string;
  /**
   * Vendor leases held right now. An empty array means none is held, which is the
   * normal state between executions.
   */
  leaseHolders: CollectorLeaseHolderView[];
  /**
   * Stated in the payload so the screen does not have to hard-code the caveat,
   * and so it changes in one place if a real failure ledger ever lands.
   */
  limits: {
    /**
     * True while nothing records a failure that outlives the transaction that
     * failed.
     *
     * **Now FALSE.** `sync_gap` gained a writer on 1 September 2026: one row per
     * outage, extended while it continues and closed on recovery, so `resolvedGaps`
     * is real history with real spans. `ingest_run` is still a success log — it
     * inserts 'running' and updates to 'ok' in ONE transaction, so a crash rolls
     * its own row away — which is why `noSuccessRate` stays true.
     */
    noFailureHistory: boolean;
    /** True while a success rate cannot be computed from what is recorded. */
    noSuccessRate: boolean;
  };
}

/* ── Document scanning: is anything actually looking at uploads? ─────
 *
 * The panel behind `GET /settings/documents/scan-health`, and the answer to a
 * question `docs/logic-pipelines.md` recorded as UNSPECIFIED: *"what happens to
 * a document stuck pending for a week."* Previously: nothing, silently, while it
 * blocked a work-order closeout.
 *
 * The type is shaped so the screen cannot imply a scanner is working when none
 * is. `scannerConfigured` is the first thing it carries for that reason — with no
 * verdict source, EVERY document is stuck, and the useful reading is "nothing is
 * scanning" rather than a list of individually late files.
 */
export interface StuckDocumentRow {
  documentId: string;
  mimeType: string;
  originalFileName: string | null;
  /**
   * Only ever `pending` or `failed`. `passed` is not stuck and `quarantined` is
   * not waiting for anything — something was found, and there is deliberately no
   * release path.
   */
  scanStatus: "pending" | "failed";
  receivedAt: string;
  hoursWaiting: number;
  /** Verdict attempts. Zero on a `pending` document nothing has looked at. */
  attempts: number;
  lastAttemptAt: string | null;
  /** The last engine message, verbatim — an unrecognised tag value lands here. */
  lastDetail: string | null;
  /** A live parent, so the row can say what this is holding up. */
  attachedTo: string | null;
}

export interface DocumentScanHealthResponse {
  /** Every document except the files imported from MaintainX, which are counted below. */
  counts: { pending: number; passed: number; failed: number; quarantined: number };
  /**
   * The files the MaintainX files import landed, counted on their own (files
   * plan, Decision 11, 2 October 2026). They sit at `pending` for ever by
   * design and open in the page on Eden's ruling, so inside `counts` about
   * 1,000 of them would read as files waiting on a verdict. Same rule as the
   * `mainx_archive` delivery basis: intake channel `migration` with a
   * `mainx_api` source row. They are not in `stuck` either.
   */
  importedFromMainx: { pending: number; passed: number; failed: number; quarantined: number };
  /**
   * The configured threshold, echoed rather than restated in the UI. Same rule
   * the upload control follows for size limits: the server knows the real value,
   * so a copy in the browser is only a way for the two to disagree.
   */
  stuckAfterHours: number;
  stuck: StuckDocumentRow[];
  /**
   * False when no verdict source is configured — which is the state today.
   *
   * The screen MUST lead with this. Nothing in TruckBuddy can produce a verdict
   * on its own, so a panel that showed a growing pending count without saying
   * why would read as a slow scanner rather than as an absent one.
   */
  scannerConfigured: boolean;
  /** True when the caller may ask for a rescan, so the button is not offered blind. */
  canRescan: boolean;
}

/* ── Checking a proposed call rate before it is sent ──────────────────
 *
 * The screen refuses what the API would refuse, so a bad number is answered in
 * place by a sentence rather than by a 400 quoting a zod path. It also WARNS
 * about two things that are legal but probably not intended, without blocking
 * them — during an incident a deliberately punishing rate is exactly what
 * somebody wants, and a screen that argued with them would be the wrong tool.
 */

/** The rate ceiling the contract accepts. Above this the API answers 400. */
export const MAX_RATE_PER_MINUTE = 10_000;

/**
 * How many requests the MaintainX detail sweep makes, and how long its Job may
 * run. Measured on this tenant: 65 part-detail plus 1,759 work-order-detail, and
 * `replicaTimeout: 7200` in `infra/modules/environment.bicep`.
 *
 * Below roughly 16 a minute the sweep cannot finish inside that window. That is
 * a warning rather than a refusal: throttling hard on purpose is a legitimate
 * thing to do to a misbehaving vendor, and the honest answer is to say what will
 * happen rather than to forbid it.
 */
export const MAINX_DETAIL_REQUESTS = 1_824;
export const MAINX_DETAIL_TIMEOUT_MINUTES = 120;

/**
 * Above this, a rate stops being a safety margin.
 *
 * No vendor publishes a read limit this repo has verified, so this is not a
 * measured ceiling — it is the point past which the control is no longer doing
 * the job it exists for. Eden, 26 August 2026: *"I dont want to be maxing out
 * these APIs, I want to avoid that like the plague."*
 */
export const RECKLESS_RATE_PER_MINUTE = 600;

export interface RateProblem {
  key: "samsaraMaxPerMinute" | "mainxMaxPerMinute" | "sylectusMaxPerMinute";
  severity: "refuse" | "warn";
  message: string;
}

/**
 * Problems with a proposed set of rates, worst first.
 *
 * A `refuse` blocks Save; a `warn` is shown and allowed. `null` is never a
 * problem — it means "use the built-in default", which is always safe.
 */
export function checkCollectorRates(rates: {
  samsaraMaxPerMinute: number | null;
  mainxMaxPerMinute: number | null;
  sylectusMaxPerMinute: number | null;
}): RateProblem[] {
  const problems: RateProblem[] = [];
  const named: Array<[RateProblem["key"], string, number | null]> = [
    ["samsaraMaxPerMinute", "Samsara", rates.samsaraMaxPerMinute],
    ["mainxMaxPerMinute", "MaintainX", rates.mainxMaxPerMinute],
    ["sylectusMaxPerMinute", "Sylectus", rates.sylectusMaxPerMinute],
  ];

  for (const [key, name, value] of named) {
    if (value === null) continue;

    if (!Number.isInteger(value)) {
      problems.push({ key, severity: "refuse", message: `${name} must be a whole number.` });
      continue;
    }
    if (value < 1) {
      problems.push({
        key,
        severity: "refuse",
        // Named rather than silently coerced: there are two ways to stop a
        // vendor and only one of them is legible to the next person.
        message: `${name} must be at least 1 a minute. To stop it entirely, switch it off above.`,
      });
      continue;
    }
    if (value > MAX_RATE_PER_MINUTE) {
      problems.push({
        key,
        severity: "refuse",
        message: `${name} cannot exceed ${MAX_RATE_PER_MINUTE.toLocaleString()} a minute.`,
      });
      continue;
    }
    if (value > RECKLESS_RATE_PER_MINUTE) {
      problems.push({
        key,
        severity: "warn",
        message:
          `${name} at ${value} a minute is high enough to risk the rate limit. ` +
          "No vendor here publishes a verified read limit, so this is a guess with " +
          "our API key as the stake.",
      });
    }
  }

  // The one warning that is about a job rather than about a vendor.
  const mainx = rates.mainxMaxPerMinute;
  const floor = Math.ceil(MAINX_DETAIL_REQUESTS / MAINX_DETAIL_TIMEOUT_MINUTES);
  if (mainx !== null && Number.isInteger(mainx) && mainx >= 1 && mainx < floor) {
    const minutes = Math.ceil(MAINX_DETAIL_REQUESTS / mainx);
    problems.push({
      key: "mainxMaxPerMinute",
      severity: "warn",
      message:
        `At ${mainx} a minute the MaintainX detail sweep (~${MAINX_DETAIL_REQUESTS.toLocaleString()} ` +
        `requests) would take about ${minutes} minutes and be cut off by its ` +
        `${MAINX_DETAIL_TIMEOUT_MINUTES}-minute limit. Fine if you are throttling on purpose — ` +
        "the run will simply not finish.",
    });
  }

  return problems.sort((a, b) => (a.severity === b.severity ? 0 : a.severity === "refuse" ? -1 : 1));
}
